import { useState, useCallback, useEffect } from 'react';

export interface SitesFilters {
  search: string;
  includeArchived: boolean;
}

/**
 * Hook to manage sites filter state with debouncing
 * Follows the pattern from useAllocationFilters but with debouncing for search
 * @returns Filter state and update functions
 */
export const useSitesFilters = () => {
  const [search, setSearchImmediate] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const updateSearch = useCallback((value: string) => {
    setSearchImmediate(value);
  }, []);

  const updateIncludeArchived = useCallback((value: boolean) => {
    setIncludeArchived(value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchImmediate('');
    setDebouncedSearch('');
  }, []);

  const filters: SitesFilters = {
    search: debouncedSearch,
    includeArchived,
  };

  return {
    // Current search value (for input binding)
    searchValue: search,
    // Debounced filters (for API calls)
    filters,
    updateSearch,
    updateIncludeArchived,
    clearSearch,
    hasActiveFilters: Boolean(debouncedSearch),
  };
};
